// src/components/common/intercom/intercomUtils.js
import agthealthService from '../../../services/agthealthService';

/**
 * Extrae información del usuario desde el token JWT
 * @returns {Object} Datos del usuario extraídos del token
 */
export const extractUserDataFromToken = () => {
  const token = localStorage.getItem('agthealth_token');
  if (token) {
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      return {
        user_id: tokenData.id || tokenData.sub,
        email: tokenData.email,
        name: tokenData.name,
        role: tokenData.role,
        is_independent: tokenData.role === 'independent_professional',
        token_expires_at: tokenData.exp
      };
    } catch (error) {
      console.error("Error al decodificar token:", error);
    }
  }
  return {};
};

/**
 * Obtiene datos completos del perfil del usuario
 * @returns {Promise<Object>} Datos del perfil del usuario
 */
export const getUserProfileData = async () => {
  try {
    const response = await agthealthService.getUserProfile(false); // No forzar recarga para evitar llamadas innecesarias
    if (response.success && response.data) {
      return {
        user_id: response.data.id,
        name: `${response.data.firstName || ''} ${response.data.lastName || ''}`.trim(),
        email: response.data.email,
        phone: response.data.phone,
        specialty: response.data.specialty,
        country: response.data.country,
        city: response.data.city,
        clinic_name: response.data.clinicName,
        identification_number: response.data.identificationNumber,
        created_at: response.data.createdAt ? new Date(response.data.createdAt).getTime() / 1000 : Math.floor(Date.now() / 1000)
      };
    }
  } catch (error) {
    console.error("Error al obtener datos de perfil:", error);
  }
  return {};
};

/**
 * Obtiene estadísticas de uso de la aplicación
 * @returns {Promise<Object>} Estadísticas de uso
 */
export const getUsageStats = async () => {
  try {
    // Obtener conteo de pacientes
    const patientsResponse = await agthealthService.getPatients();
    const patientsCount = patientsResponse.success ? patientsResponse.data.length : 0;
    
    // Obtener conteo de citas
    const appointmentsResponse = await agthealthService.getAppointments();
    const appointmentsCount = appointmentsResponse.success ? appointmentsResponse.data.length : 0;
    
    // Obtener conteo de registros
    const recordsResponse = await agthealthService.getRecords();
    const recordsCount = recordsResponse.success ? recordsResponse.data.length : 0;
    
    // Obtener tareas
    const tasksResponse = await agthealthService.getUserTasks();
    const tasksCount = tasksResponse.success ? tasksResponse.data.length : 0;
    const pendingTasksCount = tasksResponse.success 
      ? tasksResponse.data.filter(task => !task.completed).length 
      : 0;
    
    return {
      patients_count: patientsCount,
      appointments_count: appointmentsCount,
      records_count: recordsCount,
      tasks_count: tasksCount,
      pending_tasks_count: pendingTasksCount,
      app_usage_level: calculateUsageLevel(patientsCount, appointmentsCount)
    };
  } catch (error) {
    console.error("Error al obtener estadísticas de uso:", error);
  }
  return {};
};

/**
 * Calcula el nivel de uso de la aplicación
 * @param {number} patientsCount Número de pacientes
 * @param {number} appointmentsCount Número de citas
 * @returns {string} Nivel de uso (light, medium, heavy)
 */
const calculateUsageLevel = (patientsCount, appointmentsCount) => {
  if (patientsCount > 50 || appointmentsCount > 100) return "heavy";
  if (patientsCount > 10 || appointmentsCount > 20) return "medium";
  return "light";
};

/**
 * Obtiene detalles completos de la suscripción
 * @returns {Promise<Object>} Detalles de la suscripción
 */
export const getSubscriptionDetails = async () => {
  try {
    const response = await agthealthService.getSubscriptionStatus();
    if (response) {
      return {
        is_pro: response.isPro || false,
        subscription_status: response.status || 'none',
        trial_active: response.trialActive || false,
        trial_end_date: response.trialEndDate ? new Date(response.trialEndDate).getTime() / 1000 : null,
        subscription_plan: response.planName || 'free',
        subscription_price: response.amount || 0,
        payment_method: response.paymentMethod || '',
        subscription_start_date: response.startDate ? new Date(response.startDate).getTime() / 1000 : null,
        subscription_end_date: response.endDate ? new Date(response.endDate).getTime() / 1000 : null,
        stripe_customer_id: response.customerId || '',
        stripe_subscription_id: response.subscriptionId || ''
      };
    }
  } catch (error) {
    console.error("Error al obtener detalles de suscripción:", error);
  }
  return {};
};

/**
 * Obtiene datos de actividad reciente
 * @returns {Promise<Object>} Datos de actividad reciente
 */
export const getRecentActivity = async () => {
  try {
    // Obtener citas recientes
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7);
    
    const startDate = oneWeekAgo.toISOString().split('T')[0];
    const endDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      .toISOString().split('T')[0];
    
    const appointmentsResponse = await agthealthService.getAppointmentsByDateRange(startDate, endDate);
    
    // Obtener tareas recientes
    const tasksResponse = await agthealthService.getUserTasks({ completed: false });
    
    // Ordenar citas por fecha para encontrar la próxima
    let nextAppointmentDate = null;
    if (appointmentsResponse.success && appointmentsResponse.data.length > 0) {
      const upcomingAppointments = appointmentsResponse.data
        .filter(apt => new Date(apt.date) > today)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      if (upcomingAppointments.length > 0) {
        nextAppointmentDate = new Date(upcomingAppointments[0].date).getTime() / 1000;
      }
    }
    
    return {
      recent_appointments_count: appointmentsResponse.success ? appointmentsResponse.data.length : 0,
      has_upcoming_appointments: nextAppointmentDate !== null,
      next_appointment_date: nextAppointmentDate,
      pending_tasks_count: tasksResponse.success ? tasksResponse.data.length : 0,
      last_activity_date: Math.floor(Date.now() / 1000)
    };
  } catch (error) {
    console.error("Error al obtener actividad reciente:", error);
  }
  return {};
};

/**
 * Obtiene información sobre el navegador y dispositivo
 * @returns {Object} Datos del navegador y dispositivo
 */
export const getDeviceData = () => {
  if (typeof window === 'undefined') return {};
  
  return {
    browser: navigator.userAgent.match(/chrome|chromium|crios/i) ? 'chrome' : 
             navigator.userAgent.match(/firefox|fxios/i) ? 'firefox' :
             navigator.userAgent.match(/safari/i) ? 'safari' :
             navigator.userAgent.match(/edg/i) ? 'edge' : 'other',
    browser_version: getBrowserVersion(),
    os: getOperatingSystem(),
    device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    screen_size: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language || 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

/**
 * Obtiene la versión del navegador
 * @returns {string} Versión del navegador
 */
const getBrowserVersion = () => {
  const ua = navigator.userAgent;
  let tem, M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
    return (tem[1] || '');
  }
  if (M[1] === 'Chrome') {
    tem = ua.match(/\bEdg\/(\d+)/) || ua.match(/\bEdge\/(\d+)/) || ua.match(/\bOPR\/(\d+)/);
    if (tem != null) return tem[1];
  }
  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
  if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);
  return M[1];
};

/**
 * Obtiene el sistema operativo
 * @returns {string} Sistema operativo
 */
const getOperatingSystem = () => {
  const userAgent = window.navigator.userAgent;
  if (userAgent.indexOf("Windows") !== -1) return "Windows";
  if (userAgent.indexOf("Mac") !== -1) return "macOS";
  if (userAgent.indexOf("Linux") !== -1) return "Linux";
  if (userAgent.indexOf("Android") !== -1) return "Android";
  if (userAgent.indexOf("iOS") !== -1 || userAgent.indexOf("iPhone") !== -1 || userAgent.indexOf("iPad") !== -1) return "iOS";
  return "Unknown";
};

/**
 * Actualiza todos los datos en Intercom
 * @returns {Promise<Object|null>} Datos actualizados o null si hubo un error
 */
export const updateAllIntercomData = async () => {
  if (typeof window === 'undefined' || !window.Intercom) return null;
  
  try {
    // Recopilar todos los datos
    const tokenData = extractUserDataFromToken();
    const profileData = await getUserProfileData();
    const usageStats = await getUsageStats();
    const subscriptionDetails = await getSubscriptionDetails();
    const activityData = await getRecentActivity();
    const deviceData = getDeviceData();
    
    // Combinar todos los datos
    const allData = {
      ...tokenData,
      ...profileData,
      ...usageStats,
      ...subscriptionDetails,
      ...activityData,
      ...deviceData,
      app_version: process.env.REACT_APP_VERSION || '1.0.0',
      environment: process.env.NODE_ENV
    };
    
    // Actualizar en Intercom
    window.Intercom('update', allData);
    console.log('Datos actualizados en Intercom');
    
    return allData;
  } catch (error) {
    console.error('Error al actualizar datos en Intercom:', error);
    return null;
  }
};

/**
 * Registra un evento en Intercom
 * @param {string} eventName Nombre del evento
 * @param {Object} metadata Metadatos adicionales del evento
 */
export const trackIntercomEvent = (eventName, metadata = {}) => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('trackEvent', eventName, metadata);
    console.log(`Evento "${eventName}" enviado a Intercom`, metadata);
  } else {
    console.warn(`No se pudo enviar evento "${eventName}" a Intercom - No inicializado`);
  }
};